import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Typography, Button, TextField, Chip, IconButton, MenuItem, Select,
  FormControl, InputLabel, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Autocomplete, Avatar, LinearProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio,
  Chip as MuiChip
} from '@mui/material';
import { Send, AttachFile, Delete, Close, CampaignOutlined, People, CheckCircle, ErrorOutline, Visibility } from '@mui/icons-material';
import { API } from '../context/AuthContext';
import { STATUS_LABELS, fmt } from '../utils';
import { useSnackbar } from 'notistack';

const TAG_OPTIONS = []; // populated dynamically from clients if needed later

function NewCampaignTab({ onSent }) {
  const { enqueueSnackbar } = useSnackbar();
  const [audienceMode, setAudienceMode] = useState('filter'); // 'filter' | 'specific'
  const [statusFilter, setStatusFilter] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    API.get('/clients', { params: { limit: 500 } }).then(r => setClients(r.data.clients));
  }, []);

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const body = audienceMode === 'specific'
        ? { clientIds: selectedClients.map(c => c._id) }
        : { status: statusFilter || undefined };
      const { data } = await API.post('/campaigns/preview-recipients', body);
      setPreview(data);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [audienceMode, statusFilter, selectedClients]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      enqueueSnackbar('El archivo no debe superar 10MB', { variant: 'error' });
      return;
    }
    setFile(f);
  };

  const handleConfirmSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('message', message);
      if (file) formData.append('attachment', file);
      if (audienceMode === 'specific') {
        formData.append('clientIds', JSON.stringify(selectedClients.map(c => c._id)));
      } else if (statusFilter) {
        formData.append('status', statusFilter);
      }

      const { data } = await API.post('/campaigns/send', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      enqueueSnackbar(`Campaña enviándose a ${data.totalRecipients} destinatario(s)`, { variant: 'success' });
      setSubject(''); setMessage(''); setFile(null); setSelectedClients([]); setStatusFilter('');
      onSent?.();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al enviar la campaña', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const canSend = subject.trim() && message.trim() && (preview?.count || 0) > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 720 }}>
      {/* Audience selection */}
      <Card sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <People fontSize="small" color="primary" /> Destinatarios
        </Typography>

        <RadioGroup row value={audienceMode} onChange={e => setAudienceMode(e.target.value)} sx={{ mb: 2 }}>
          <FormControlLabel value="filter" control={<Radio size="small" />} label="Filtrar por estado" />
          <FormControlLabel value="specific" control={<Radio size="small" />} label="Elegir clientes específicos" />
        </RadioGroup>

        {audienceMode === 'filter' ? (
          <FormControl fullWidth size="small">
            <InputLabel>Estado del cliente</InputLabel>
            <Select value={statusFilter} label="Estado del cliente" onChange={e => setStatusFilter(e.target.value)}>
              <MenuItem value="">Todos los clientes activos</MenuItem>
              <MenuItem value="prospecto">Solo prospectos</MenuItem>
              <MenuItem value="activo">Solo activos</MenuItem>
              <MenuItem value="vip">Solo VIP</MenuItem>
              <MenuItem value="inactivo">Solo inactivos</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <Autocomplete
            multiple
            options={clients}
            value={selectedClients}
            onChange={(_, val) => setSelectedClients(val)}
            getOptionLabel={(c) => c.name}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderInput={(params) => <TextField {...params} size="small" label="Buscar y seleccionar clientes" placeholder="Escribe un nombre..." />}
            renderOption={(props, c) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>{c.name?.charAt(0)}</Avatar>
                <Box>
                  <Typography variant="body2">{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.email || 'sin correo'}</Typography>
                </Box>
              </Box>
            )}
          />
        )}

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {previewLoading ? (
            <CircularProgress size={16} />
          ) : preview ? (
            <Chip
              size="small"
              color={preview.count > 0 ? 'success' : 'default'}
              label={`${preview.count} destinatario(s) con correo registrado`}
            />
          ) : null}
        </Box>
        {preview?.count === 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Ningún cliente coincide con este filtro o no tienen correo registrado.
          </Alert>
        )}
      </Card>

      {/* Message content */}
      <Card sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Contenido del correo</Typography>
        <TextField
          label="Asunto"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          fullWidth size="small" sx={{ mb: 2 }}
          placeholder="Ej. Promoción especial de junio — IDN Innovación"
        />
        <TextField
          label="Mensaje"
          value={message}
          onChange={e => setMessage(e.target.value)}
          fullWidth multiline minRows={6}
          placeholder="Escribe el cuerpo del correo. Se enviará junto con el saludo y el brochure adjunto (si seleccionas uno)."
        />

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<AttachFile />}
            size="small"
          >
            {file ? 'Cambiar archivo' : 'Adjuntar brochure (PDF o imagen)'}
            <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleFileChange} />
          </Button>
          {file && (
            <Chip
              sx={{ ml: 1.5 }}
              label={file.name}
              onDelete={() => setFile(null)}
              deleteIcon={<Close fontSize="small" />}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
          disabled={!canSend || sending}
          onClick={() => setConfirmOpen(true)}
        >
          {sending ? 'Enviando...' : 'Enviar campaña'}
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar envío</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Estás a punto de enviar este correo a <strong>{preview?.count || 0} destinatario(s)</strong>.
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmSend}>Confirmar y enviar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function HistoryTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/campaigns');
      setCampaigns(data.campaigns);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  // Poll while any campaign is still "enviando"
  useEffect(() => {
    const hasSending = campaigns.some(c => c.status === 'enviando');
    if (!hasSending) return;
    const interval = setInterval(fetchCampaigns, 4000);
    return () => clearInterval(interval);
  }, [campaigns, fetchCampaigns]);

  const viewDetail = async (id) => {
    try {
      const { data } = await API.get(`/campaigns/${id}`);
      setDetail(data);
    } catch {
      enqueueSnackbar('Error al cargar detalle', { variant: 'error' });
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('¿Eliminar esta campaña del historial?')) return;
    try {
      await API.delete(`/campaigns/${id}`);
      enqueueSnackbar('Campaña eliminada', { variant: 'success' });
      fetchCampaigns();
    } catch {
      enqueueSnackbar('Error al eliminar', { variant: 'error' });
    }
  };

  const statusChip = (c) => {
    if (c.status === 'enviando') return <Chip size="small" color="info" label={`Enviando ${c.sentCount + c.errorCount}/${c.totalRecipients}`} />;
    if (c.status === 'completada') return <Chip size="small" color="success" label="Completada" icon={<CheckCircle fontSize="small" />} />;
    if (c.status === 'con_errores') return <Chip size="small" color="warning" label={`${c.errorCount} con error`} icon={<ErrorOutline fontSize="small" />} />;
    return <Chip size="small" label="Borrador" />;
  };

  return (
    <Box>
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Asunto</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Enviado por</TableCell>
              <TableCell align="center">Destinatarios</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : campaigns.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>Sin campañas enviadas todavía</TableCell></TableRow>
            ) : campaigns.map(c => (
              <TableRow key={c._id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{c.subject}</Typography>
                  {c.attachmentName && (
                    <Typography variant="caption" color="text.secondary">📎 {c.attachmentName}</Typography>
                  )}
                </TableCell>
                <TableCell><Typography variant="body2">{fmt.dateTime(c.createdAt)}</Typography></TableCell>
                <TableCell><Typography variant="body2">{c.createdBy?.name || '-'}</Typography></TableCell>
                <TableCell align="center">{c.totalRecipients}</TableCell>
                <TableCell>{statusChip(c)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver detalle">
                    <IconButton size="small" onClick={() => viewDetail(c._id)}><Visibility fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar del historial">
                    <IconButton size="small" color="error" onClick={() => deleteCampaign(c._id)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>{detail.subject}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{detail.message}</Typography>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Destinatarios ({detail.recipients.length})</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>Cliente</TableCell><TableCell>Correo</TableCell><TableCell>Estado</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {detail.recipients.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>
                        {r.status === 'enviado' && <Chip size="small" color="success" label="Enviado" />}
                        {r.status === 'error' && <Tooltip title={r.error}><Chip size="small" color="error" label="Error" /></Tooltip>}
                        {r.status === 'pendiente' && <Chip size="small" label="Pendiente" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetail(null)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default function CampaignsPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} color="#1E1B2E" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CampaignOutlined sx={{ fontSize: '2rem', color: '#7C3AED' }} /> Marketing
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Envía promociones, brochures e invitaciones de compra a tus clientes
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Nueva campaña" />
        <Tab label="Historial" />
      </Tabs>

      {tab === 0 ? <NewCampaignTab onSent={() => setTab(1)} /> : <HistoryTab />}
    </Box>
  );
}
